import type { Section } from '@yawi/types';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { safeStorage } from '@/shared/utils/safeStorage';
import { setActiveSectionId } from '@/shared/infrastructure/repositories';
import { useAuth } from './AuthContext';
import { useDI } from './DIContext';

const STORAGE_KEY_PREFIX = 'hommi_selected_section:';

export interface SectionContextType {
  /** Catálogo de secciones visibles para el usuario (vacío si el tenant no tiene secciones). */
  sections: Section[];
  /** true solo cuando el tenant tiene secciones — controla si renderizar el selector. */
  hasSections: boolean;
  /** true cuando el usuario es SECTION_ADMIN (su catálogo ya está acotado por el backend). */
  isSectionAdmin: boolean;
  /**
   * Filtro activo. null = "todas las secciones" (contenido global + todas las visibles).
   * Para el SECTION_ADMIN es la sección activa del selector global: nunca null si
   * tiene secciones, y viaja al backend como `x-section-id`.
   */
  selectedSectionId: string | null;
  setSelectedSectionId: (sectionId: string | null) => void;
  loading: boolean;
  /** Vuelve a pedir el catálogo: se carga una sola vez por sesión y no ve las secciones creadas después. */
  refreshSections: () => void;
}

const SectionContext = createContext<SectionContextType | null>(null);

export const SectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const { sectionRepo } = useDI();
  const storageKey = currentUser ? `${STORAGE_KEY_PREFIX}${currentUser.id}` : null;

  const [sections, setSections] = useState<Section[]>([]);
  const [selectedSectionId, setSelectedSectionIdState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const isSectionAdmin =
    currentUser?.tenantRole === 'SECTION_ADMIN' || currentUser?.role === 'SECTION_ADMIN';

  useEffect(() => {
    let cancelled = false;
    const userId = currentUser?.id;
    if (!userId) setActiveSectionId(null); // sesión cerrada: no arrastrar la sección al siguiente usuario
    setLoading(true);

    sectionRepo
      .getAllSections()
      .then((catalog) => {
        if (cancelled) return;
        setSections(catalog ?? []);

        // El SECTION_ADMIN siempre opera sobre una sección: la guardada si sigue
        // siendo suya, si no la primera (§25.7).
        const stored = userId ? safeStorage.getItem(`${STORAGE_KEY_PREFIX}${userId}`) : null;
        const storedIsValid = stored && catalog.some((section) => section.id === stored);
        const next = storedIsValid ? stored : isSectionAdmin ? (catalog[0]?.id ?? null) : null;
        // Antes del render: las páginas que se montan con la sección ya piden con el header.
        setActiveSectionId(isSectionAdmin ? next : null);
        setSelectedSectionIdState(next);
      })
      .catch(() => {
        if (!cancelled) {
          setSections([]);
          setActiveSectionId(null);
          setSelectedSectionIdState(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, sectionRepo, reloadKey]);

  const setSelectedSectionId = (sectionId: string | null) => {
    if (isSectionAdmin) {
      if (!sectionId) return;
      setActiveSectionId(sectionId);
    }
    setSelectedSectionIdState(sectionId);
    if (storageKey) {
      safeStorage.setItem(storageKey, sectionId ?? '');
    }
  };

  const value: SectionContextType = useMemo(
    () => ({
      sections,
      hasSections: sections.length > 0,
      isSectionAdmin,
      selectedSectionId,
      setSelectedSectionId,
      loading,
      refreshSections: () => setReloadKey((key) => key + 1),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sections, isSectionAdmin, selectedSectionId, loading]
  );

  return <SectionContext.Provider value={value}>{children}</SectionContext.Provider>;
};

export const useSection = (): SectionContextType => {
  const ctx = useContext(SectionContext);
  if (!ctx) throw new Error('useSection must be used within a SectionProvider');
  return ctx;
};